// Dashboard View Component - displays all roadmaps and option to create new ones
import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash, Book, Timer, BarChart3, Star, Info, Flame } from 'lucide-react';
import type { RoadmapData } from '../../types';
import { useSubscription } from '../../hooks/useSubscription';
import { ExpertRoadmapsSection } from '../dashboard/ExpertRoadmapsSection';
import { convertAdminRoadmapToRoadmapData } from '../../services/adminRoadmapConverter';
import type { AdminRoadmapWithDays } from '../../services/adminRoadmapService';

interface DashboardViewProps {
  roadmaps: RoadmapData[];
  onCreateNew: () => void;
  onSelectRoadmap: (roadmap: RoadmapData) => void;
  onSelectAdminRoadmap?: (adminRoadmap: AdminRoadmapWithDays) => void;
  onDeleteRoadmap: (id: string) => void;
  theme: 'light' | 'dark';
  streakCount?: number;
  userEmail?: string | null;
  onUpgradeClick?: () => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  roadmaps,
  onCreateNew,
  onSelectRoadmap,
  onSelectAdminRoadmap,
  onDeleteRoadmap,
  theme,
  streakCount,
  userEmail,
  onUpgradeClick
}) => {
  const isDark = theme === 'dark';
  const { subscription } = useSubscription(userEmail);
  const [quotaUsage, setQuotaUsage] = useState({
    created: 0,
    remaining: 0,
    percentage: 0
  });

  // Handler for expert roadmaps - convert admin roadmap to user roadmap format
  const handleExpertRoadmapSelect = (adminRoadmap: AdminRoadmapWithDays) => {
    // Store the admin roadmap reference for video/content loading
    onSelectAdminRoadmap?.(adminRoadmap);
    // Convert admin roadmap to RoadmapData format and select it
    const convertedRoadmap = convertAdminRoadmapToRoadmapData(adminRoadmap);
    onSelectRoadmap(convertedRoadmap);
  };

  useEffect(() => {
    if (!subscription?.isPro) {
      const created = subscription?.roadmapCount || 0;
      const limit = 3;
      const remaining = Math.max(0, limit - created);
      const percentage = (created / limit) * 100;
      
      setQuotaUsage({
        created,
        remaining,
        percentage: Math.min(100, percentage)
      });
    }
  }, [subscription]);

  return (
    <div className="space-y-6 md:space-y-8 lg:space-y-12 animate-in pb-24 md:pb-8">
      {/* Hero Section - Mobile First */}
      <div className="relative px-0">
        <div className="space-y-3 md:space-y-4 lg:space-y-6">
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20">
            <span className="text-lg">🔥</span>
            <span className="text-xs md:text-sm font-medium text-red-600 dark:text-red-400">Build Your Identity</span>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.1]">
            <span className="bg-gradient-to-r from-red-600 via-orange-600 to-rose-600 bg-clip-text text-transparent">Master Anything</span>
            <br />
            <span className={isDark ? 'text-white' : 'text-zinc-900'}>Without Breaking Your Momentum</span>
          </h1>
          
          <p className={`text-sm md:text-base lg:text-xl max-w-2xl leading-relaxed font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
            Create personalized learning paths. Keep your streak alive. Own your progress.
            Become the learner you know you can be.
          </p>

          {/* Stats - Mobile First Grid */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 md:gap-4 lg:gap-8 pt-3 md:pt-4 lg:pt-6 justify-center sm:justify-start">
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <Book className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />
              </div>
              <div>
                <p className={`text-base sm:text-lg md:text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{roadmaps.length}</p>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Paths Created</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                <BarChart3 className="w-4 sm:w-5 h-4 sm:h-5 text-orange-500" />
              </div>
              <div>
                <p className={`text-base sm:text-lg md:text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {roadmaps.reduce((acc, r) => acc + ((r.completedDays || []).length), 0)}
                </p>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Days Mastered</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                <Flame className="w-4 sm:w-5 h-4 sm:h-5 text-orange-500" />
              </div>
              <div>
                <p className={`text-base sm:text-lg md:text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>🔥 {streakCount || 0}</p>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Days Your Identity Grows</p>
              </div>
            </div>
          </div>

          {/* Quota Counter - For non-Pro users */}
          {!subscription?.isPro && (
            <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950 dark:to-rose-950 rounded-lg border border-red-200 dark:border-red-800">
              {/* Quota Status */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                  Your Learning Path
                </span>
                <span className="text-lg font-bold text-red-600 dark:text-red-400">
                  {quotaUsage.remaining} left
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-red-200 dark:bg-red-900 rounded-full h-2 mb-3">
                <div
                  className="bg-red-600 dark:bg-red-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${quotaUsage.percentage}%` }}
                />
              </div>
              
              {/* Messaging Based on Quota Usage */}
              {quotaUsage.remaining === 3 && (
                <p className="text-xs text-zinc-600 dark:text-zinc-300 font-medium">
                  🎯 You're building foundations. <strong>Make your 3 count.</strong>
                </p>
              )}
              
              {quotaUsage.remaining === 2 && (
                <p className="text-xs text-blue-600 dark:text-blue-300 font-medium">
                  💪 <strong>2 left this month.</strong> Choose wisely.
                </p>
              )}
              
              {quotaUsage.remaining === 1 && (
                <p className="text-xs text-amber-600 dark:text-amber-300 font-medium">
                  ⚡ <strong>Final one this month.</strong> After this, you stop.
                </p>
              )}
              
              {quotaUsage.remaining === 0 && (
                <p className="text-xs text-red-600 dark:text-red-300 font-bold">
                  🔥 <strong>You've hit your limit.</strong> One more? Unlock it.
                </p>
              )}
            </div>
          )}

          {/* Streak Protection Section */}
          {streakCount && streakCount >= 10 && !subscription?.isPro && (
            <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-rose-50 dark:from-orange-950 dark:to-rose-950 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔥</span>
                <span className="text-sm font-bold text-red-700 dark:text-red-300">
                  {streakCount} Day Identity
                </span>
              </div>
              
              <p className="text-xs text-zinc-700 dark:text-zinc-300 mb-3 font-medium">
                {streakCount < 14 ? (
                  <>Your brain is rewiring. <strong>One paywall could break it all.</strong></>
                ) : streakCount < 30 ? (
                  <>Only {streakCount} days between you and the 1%. <strong>Don't lose this now.</strong></>
                ) : (
                  <><strong>{streakCount} days. That's legendary.</strong> A single mistake costs everything.</>
                )}
              </p>
              
              {onUpgradeClick && (
                <button
                  onClick={onUpgradeClick}
                  className="w-full px-3 py-2 bg-orange-600 dark:bg-orange-500 text-white text-sm font-medium rounded hover:bg-orange-700 dark:hover:bg-orange-600 transition-colors"
                >
                  Protect This Streak →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Roadmaps Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className={`text-xl md:text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
            Your Learning Identity
          </h2>
          <button
            onClick={onCreateNew}
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl font-medium text-sm hover:opacity-90 transition-all shadow-lg shadow-red-500/25 btn-glow"
          >
            <PlusCircle className="w-4 h-4" />
            🔥 Build New Path
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 stagger-children">
          {/* Create New Roadmap Card */}
          <button
            onClick={onCreateNew}
            className={`group min-h-[240px] md:min-h-[280px] rounded-3xl border-2 border-dashed flex flex-col items-center justify-center gap-4 transition-all ${
              isDark 
                ? 'border-white/10 hover:border-red-500/50 hover:bg-red-500/5' 
                : 'border-zinc-200 hover:border-red-300 hover:bg-red-50/50'
            }`}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center shadow-xl shadow-red-500/30 group-hover:scale-110 transition-transform">
              <PlusCircle className="w-8 h-8 text-white" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-red-600">🔥 Build New Learning Path</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                Master something new & strengthen your identity
              </p>
            </div>
          </button>
          {/* Roadmap Cards */}
          {roadmaps.map((roadmap) => {
            const totalDays = (roadmap.months || []).reduce((acc, month) => {
              return acc + ((month.weeks || []).reduce((weekAcc, week) => weekAcc + ((week.days || []).length), 0));
            }, 0);
            const completedCount = (roadmap.completedDays || []).length;
            const completedPercentage = totalDays > 0 ? Math.round((completedCount / totalDays) * 100) : 0;

            return (
              <div
                key={roadmap.id}
                onClick={() => onSelectRoadmap(roadmap)}
                className={`group relative min-h-[240px] md:min-h-[280px] rounded-3xl p-6 cursor-pointer transition-all card-lift overflow-hidden ${
                  isDark 
                    ? 'bg-white/[0.03] border border-white/5 hover:bg-white/[0.06]' 
                    : 'bg-white border border-zinc-200/80 hover:border-zinc-300 shadow-sm hover:shadow-xl'
                }`}
              >
                {/* Gradient accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-rose-500" />
                
                {/* Content */}
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider ${
                          isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                        }`}>
                          {roadmap.intensity}
                        </span>
                        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold ${
                          isDark ? 'bg-white/5 text-zinc-400' : 'bg-zinc-100 text-zinc-500'
                        }`}>
                          {roadmap.lang}
                        </span>
                      </div>
                      <h3 className={`text-lg font-bold leading-snug line-clamp-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                        {roadmap.title}
                      </h3>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        roadmap.id && onDeleteRoadmap(roadmap.id);
                      }}
                      className={`p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-all ${
                        isDark ? 'hover:bg-red-500/20 text-zinc-400 hover:text-red-400' : 'hover:bg-red-50 text-zinc-400 hover:text-red-500'
                      }`}
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Meta */}
                  <div className={`flex items-center gap-4 text-sm mb-auto ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                    <div className="flex items-center gap-1.5">
                      <Book className="w-4 h-4" />
                      <span>{roadmap.months?.length || 0} months</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Timer className="w-4 h-4" />
                      <span>{roadmap.meta?.studyTime || 2}h/day</span>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Progress</span>
                      <span className="font-semibold text-red-500">{completedPercentage}%</span>
                    </div>
                    <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-zinc-100'}`}>
                      <div
                        className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full transition-all duration-700"
                        style={{ width: `${completedPercentage}%` }}
                      />
                    </div>
                    <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      {completedCount} of {totalDays} days completed
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {roadmaps.length === 0 && (
          <div className={`text-center py-16 rounded-3xl border-2 border-dashed ${
            isDark ? 'border-white/10' : 'border-zinc-200'
          }`}>
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-red-600/20 to-orange-600/20 flex items-center justify-center">
              <Book className={`w-10 h-10 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              🔥 Start Building Your Identity
            </h3>
            <p className={`max-w-md mx-auto mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              Every path you create becomes part of who you are. Create your first learning path and transform yourself forever.
            </p>
            <button
              onClick={onCreateNew}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl font-medium hover:opacity-90 transition-all shadow-lg shadow-red-500/25"
            >
              <PlusCircle className="w-5 h-5" />
              🔥 Build Your First Path
            </button>
          </div>
        )}

        {/* Expert Roadmaps Section */}
        <ExpertRoadmapsSection 
          isDark={isDark}
          onSelectRoadmap={handleExpertRoadmapSelect}
          userEmail={userEmail}
        />
      </div>
    </div>
  );
};
